import { z } from 'zod';

import type { TranslationKey } from '@/i18n-key';
import type { CareerStep, CareerStepKind, CareerStepRequest } from '@/types/career-growth';

import { readRefusal } from '@/api/api';
import { CAREER_STEP_KINDS } from '@/types/career-growth';
import { toIsoDate } from '@/utils/dateUtils';

/**
 * The shape and the rules of one career step's form, shared by the editor modal and its tests.
 * Messages are translation keys, resolved where the field is rendered, so the schema stays free
 * of the `t` of a given component.
 */

const TITLE_REQUIRED_MESSAGE: TranslationKey =
    'performanceProfile.growth.stepsEditor.titleRequired';
const START_DATE_REQUIRED_MESSAGE: TranslationKey =
    'performanceProfile.growth.stepsEditor.startDateRequired';
const TITLE_TOO_LONG_MESSAGE: TranslationKey = 'performanceProfile.growth.stepsEditor.titleTooLong';
const END_BEFORE_START_MESSAGE: TranslationKey =
    'performanceProfile.growth.stepsEditor.endBeforeStart';
const END_DATE_REQUIRED_MESSAGE: TranslationKey =
    'performanceProfile.growth.stepsEditor.endDateRequired';
const PAST_END_IN_FUTURE_MESSAGE: TranslationKey =
    'performanceProfile.growth.stepsEditor.pastEndInFuture';

/** Mirrors the column the API stores the title in; the input caps the typing at the same bound. */
export const CAREER_STEP_TITLE_MAX_LENGTH = 200;

/** The steps that are still running: an end date would contradict what they mean. */
const OPEN_ENDED_KINDS: ReadonlySet<CareerStepKind> = new Set<CareerStepKind>([
    'Current',
    'Target',
]);

export const isOpenEndedKind = (kind: CareerStepKind) => OPEN_ENDED_KINDS.has(kind);

/** The translated name of a step kind, shared by the kind picker and the list's pills. */
export const careerStepKindLabelKey = (kind: CareerStepKind): TranslationKey =>
    `performanceProfile.growth.stepsEditor.kinds.${kind}`;

const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

const isoDate = (message: TranslationKey) => z.string().regex(ISO_DATE_PATTERN, { message });

/**
 * A position already held is known by its name; a target may be known by the competency profile
 * it requires alone, when the employee aims to grow in their role rather than change it. The API
 * applies the same rule.
 */
export const isTitleRequired = ({
    kind,
    competencyProfileId,
}: {
    kind: CareerStepKind;
    competencyProfileId: string | null;
}) => kind !== 'Target' || competencyProfileId === null;

export const careerStepSchema = z
    .object({
        // The max is a backstop: `TextInput` already stops the typing at
        // `CAREER_STEP_TITLE_MAX_LENGTH`, so only a pasted-then-programmatic value can reach it.
        title: z
            .string()
            .trim()
            .max(CAREER_STEP_TITLE_MAX_LENGTH, { message: TITLE_TOO_LONG_MESSAGE }),
        kind: z.enum(CAREER_STEP_KINDS),
        startDate: isoDate(START_DATE_REQUIRED_MESSAGE).nullable(),
        endDate: isoDate(END_BEFORE_START_MESSAGE).nullable(),
        competencyProfileId: z.string().nullable(),
    })
    .refine((values) => values.title.length > 0 || !isTitleRequired(values), {
        message: TITLE_REQUIRED_MESSAGE,
        path: ['title'],
    })
    // A target may leave its horizon open; a step already lived always has a start.
    .refine((values) => values.kind === 'Target' || values.startDate !== null, {
        message: START_DATE_REQUIRED_MESSAGE,
        path: ['startDate'],
    })
    // A past position is closed by definition: without its end it would read as the current one.
    .refine((values) => values.kind !== 'Past' || values.endDate !== null, {
        message: END_DATE_REQUIRED_MESSAGE,
        path: ['endDate'],
    })
    // A position already left cannot have ended after today.
    // `YYYY-MM-DD` sorts lexicographically, so days compare without parsing a `Date`.
    .refine(
        (values) =>
            values.kind !== 'Past' || values.endDate === null || values.endDate <= todayIsoDate(),
        {
            message: PAST_END_IN_FUTURE_MESSAGE,
            path: ['endDate'],
        },
    )
    .refine(
        (values) =>
            values.endDate === null ||
            values.startDate === null ||
            values.endDate >= values.startDate,
        {
            message: END_BEFORE_START_MESSAGE,
            path: ['endDate'],
        },
    );

export type CareerStepFormValues = z.infer<typeof careerStepSchema>;

/**
 * A target is aimed at a year, not at a day, so its horizon is edited as a year while the timeline
 * and the API keep reading the `YYYY-MM-DD` every other step is stored in. These two functions are
 * the only place the two spellings meet: January 1st is the day that stands for the year.
 */
export const horizonYearOf = (isoDate: string | null): number | null =>
    isoDate === null ? null : Number(isoDate.slice(0, 4));

export const horizonDateOf = (year: number | null): string | null =>
    year === null ? null : `${year}-01-01`;

/** How far ahead the horizon can be aimed, past the year the editor is opened in. */
export const HORIZON_YEARS_AHEAD = 10;

/**
 * What a brand new step starts from: the kind the caller asked for. Its dates are left for the
 * reader to pick -- no default day fits a position already left, one held today and a target alike.
 */
export interface CareerStepFormDefaults {
    kind: CareerStepKind;
    competencyProfileId: string | null;
}

export const todayIsoDate = () => toIsoDate(new Date());

/** The form's values for an existing step, or the defaults of a new one. */
export const toFormValues = (
    step: CareerStep | null,
    defaults: CareerStepFormDefaults,
): CareerStepFormValues =>
    step
        ? {
              title: step.title,
              kind: step.kind,
              startDate: step.startDate,
              endDate: step.endDate,
              competencyProfileId: step.competencyProfileId,
          }
        : {
              title: '',
              kind: defaults.kind,
              startDate: null,
              endDate: null,
              competencyProfileId: defaults.competencyProfileId,
          };

/**
 * The payload the API expects. A current or target step is open-ended by definition, so an end
 * date left over from a kind change never travels.
 */
export const toCareerStepRequest = (values: CareerStepFormValues): CareerStepRequest => ({
    title: values.title.trim(),
    kind: values.kind,
    startDate: values.startDate,
    endDate: isOpenEndedKind(values.kind) ? null : values.endDate,
    competencyProfileId: values.competencyProfileId,
});

/**
 * The API answers a refused step with `ValidationProblemDetails` keyed on the C# property name and
 * a developer-English sentence. The key is the stable half, so it is what we read, and the reason
 * shown is ours, translated.
 */
const REFUSED_FIELD_MESSAGES: Record<string, TranslationKey> = {
    title: TITLE_REQUIRED_MESSAGE,
    startdate: START_DATE_REQUIRED_MESSAGE,
    enddate: END_DATE_REQUIRED_MESSAGE,
};

/** The readable reason of a validation refusal, or `null` when the failure is not one. */
export const toRefusalMessage = (error: unknown): TranslationKey | null => {
    const refusal = readRefusal(error);

    if (refusal?.status !== 400) {
        return null;
    }

    const refusedField = Object.keys(refusal.fieldErrors)
        .map((field) => field.toLowerCase())
        .find((field) => Object.hasOwn(REFUSED_FIELD_MESSAGES, field));

    return refusedField ? REFUSED_FIELD_MESSAGES[refusedField] : null;
};
